import React from 'react';
import { XMarkIcon, DocumentTextIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const APIResponseModal = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  const { type, requestData, responseData, success, timestamp } = data || {};

  const formatJSON = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return String(obj);
    }
  };

  const getStatusIcon = () => {
    if (success) {
      return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
    }
    return <ExclamationCircleIcon className="h-6 w-6 text-red-500" />;
  };

  const getStatusColor = () => {
    return success ? 'text-green-600' : 'text-red-600';
  };

  const getStatusBg = () => {
    return success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-gray-200 flex items-center justify-between ${getStatusBg()}`}>
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h3 className={`text-lg font-semibold ${getStatusColor()}`}>
                FBR API {type === 'validate' ? 'Validation' : 'Submission'} Response
              </h3>
              <p className="text-sm text-gray-600">
                {timestamp && new Date(timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Request Data */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="h-5 w-5 text-blue-500" />
                <h4 className="text-lg font-medium text-gray-900">Request Data Sent to FBR</h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                  {formatJSON(requestData)}
                </pre>
              </div>
            </div>

            {/* Response Data */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="h-5 w-5 text-purple-500" />
                <h4 className="text-lg font-medium text-gray-900">Response from FBR API</h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                  {formatJSON(responseData)}
                </pre>
              </div>
            </div>
          </div>

          {/* Status Summary */}
          {responseData && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className="text-lg font-medium text-gray-900 mb-3">Response Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {type === 'validate' && responseData.validation && (
                  <>
                    <div>
                      <span className="font-medium text-gray-600">Status Code:</span>
                      <span className={`ml-2 ${responseData.validation.validationResponse?.statusCode === '00' ? 'text-green-600' : 'text-red-600'}`}>
                        {responseData.validation.validationResponse?.statusCode || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Message:</span>
                      <span className="ml-2 text-gray-800">
                        {responseData.validation.validationResponse?.message || 
                         responseData.validation.validationResponse?.statusMessage || 'N/A'}
                      </span>
                    </div>
                    {responseData.validation.validationResponse?.errors && responseData.validation.validationResponse.errors.length > 0 && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-600">Errors:</span>
                        <ul className="ml-2 mt-1 text-red-600">
                          {responseData.validation.validationResponse.errors.map((error, index) => (
                            <li key={index}>• {error.description || error.message || error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
                
                {type === 'submit' && responseData.submission && (
                  <>
                    <div>
                      <span className="font-medium text-gray-600">Status Code:</span>
                      <span className={`ml-2 ${responseData.submission.submissionResponse?.statusCode === '00' || responseData.submission.statusCode === '00' ? 'text-green-600' : 'text-red-600'}`}>
                        {responseData.submission.submissionResponse?.statusCode || responseData.submission.statusCode || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Message:</span>
                      <span className="ml-2 text-gray-800">
                        {responseData.submission.submissionResponse?.message || 
                         responseData.submission.message || 'N/A'}
                      </span>
                    </div>
                    {responseData.submission.submissionResponse?.invoiceNumber && (
                      <div>
                        <span className="font-medium text-gray-600">Invoice Number:</span>
                        <span className="ml-2 text-green-600 font-mono">
                          {responseData.submission.submissionResponse.invoiceNumber}
                        </span>
                      </div>
                    )}
                    {responseData.submission.submissionResponse?.qrCode && (
                      <div>
                        <span className="font-medium text-gray-600">QR Code:</span>
                        <span className="ml-2 text-blue-600 font-mono text-xs">
                          {responseData.submission.submissionResponse.qrCode}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default APIResponseModal;